<template>
  <div class="trial-lockout-overlay" :style="overlayStyle">
    <div class="trial-lockout-modal" role="dialog" aria-labelledby="trial-lockout-title">
      <div class="trial-lockout-icon" aria-hidden="true">
        <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </div>
      <h2 id="trial-lockout-title" class="trial-lockout-title">Your trial has ended</h2>
      <p class="trial-lockout-sub">
        Pick a plan to resume access to your dashboard, alerts, and tracking. Your data is safe —
        service resumes the moment you complete checkout.
      </p>

      <div class="trial-lockout-plans">
        <button
          type="button"
          :class="['trial-lockout-plan', { selected: selectedPlanKey === 'starter' }]"
          :disabled="busy"
          @click="startStarterUpgradeFlow"
        >
          <span v-if="selectedPlanKey === 'starter'" class="trial-lockout-plan-badge">Your selection</span>
          <div class="trial-lockout-plan-name">Starter</div>
          <div class="trial-lockout-plan-price">$19<span class="per">/mo</span></div>
        </button>
        <button
          type="button"
          :class="['trial-lockout-plan', 'recommended', { selected: selectedPlanKey === 'pro' }]"
          :disabled="busy"
          @click="startProUpgradeFlow"
        >
          <span v-if="selectedPlanKey === 'pro'" class="trial-lockout-plan-badge">Your selection</span>
          <span v-else class="trial-lockout-plan-badge">★ Recommended</span>
          <div class="trial-lockout-plan-name">Pro</div>
          <div class="trial-lockout-plan-price">$49<span class="per">/mo</span></div>
        </button>
      </div>

      <button type="button" class="trial-lockout-primary" :disabled="busy" @click="startProUpgradeFlow">
        {{ busy ? 'Please wait…' : 'Continue with Pro →' }}
      </button>

      <p v-if="errorMessage" class="trial-lockout-error">{{ errorMessage }}</p>

      <p class="trial-lockout-foot">
        <button type="button" :disabled="busy" @click="openComparePlans">Compare plans</button>
        ·
        <button type="button" :disabled="busy" @click="signOut">Log out</button>
      </p>
    </div>

    <div
      v-if="comparePlansOpen"
      class="trial-compare-backdrop"
      role="presentation"
      @click.self="closeComparePlans"
    >
      <div
        class="trial-compare-modal"
        role="dialog"
        aria-labelledby="trial-compare-title"
        aria-modal="true"
        @click.stop
      >
        <button type="button" class="trial-compare-close" aria-label="Close" @click="closeComparePlans">
          <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <h2 id="trial-compare-title" class="trial-compare-title">Compare plans</h2>
        <p class="trial-compare-sub">Pick the plan that fits your order volume. You can change plans anytime from billing.</p>

        <div class="trial-compare-grid">
          <div :class="['trial-compare-col', { selected: selectedPlanKey === 'starter' }]">
            <span v-if="selectedPlanKey === 'starter'" class="trial-compare-badge">Your selection</span>
            <h3 class="trial-compare-plan-name">Starter</h3>
            <p class="trial-compare-price">$19<span class="per">/mo</span></p>
            <ul class="trial-compare-features">
              <li>All alert types · real-time delivery</li>
              <li>Directional signals from Day 1</li>
              <li><strong>Pattern insights from Day 15</strong></li>
              <li>Action signals from Day 30+</li>
              <li>COGS, floor price &amp; margin alerts</li>
            </ul>
            <button
              type="button"
              class="trial-compare-btn trial-compare-btn-outline"
              :disabled="busy"
              @click="onCompareSelectStarter"
            >
              Choose Starter
            </button>
          </div>

          <div :class="['trial-compare-col', 'trial-compare-col-pro', { selected: selectedPlanKey === 'pro' }]">
            <span v-if="selectedPlanKey === 'pro'" class="trial-compare-badge">Your selection</span>
            <span v-else class="trial-compare-badge">★ Recommended</span>
            <h3 class="trial-compare-plan-name">Pro</h3>
            <p class="trial-compare-price">$49<span class="per">/mo</span></p>
            <p class="trial-compare-plus">Everything in Starter, plus:</p>
            <ul class="trial-compare-features">
              <li><strong>Competitor seller profiles</strong></li>
              <li><strong>Seller name, location &amp; storefront</strong></li>
            </ul>
            <!-- <p class="trial-compare-footnote">
              Overage: <strong>+$8 per 1,000 orders</strong><br>
              Additional storefronts: <strong>+$29/mo each</strong>
            </p> -->
            <button
              type="button"
              class="trial-compare-btn trial-compare-btn-primary"
              :disabled="busy"
              @click="onCompareSelectPro"
            >
              {{ busy ? 'Please wait…' : 'Continue with Pro →' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/TrialLockout.css'
import {
  resolveHasPaymentMethod,
  upgradeSubscriptionToPlan,
  upgradeSubscriptionToPro
} from '../../utils/billingUpgrade'
import { displaySelectedPlanKeyFromContext, isProPlanFromSummary } from '../../utils/subscriptionBilling'

export default {
  name: 'TrialLockoutOverlay',
  props: {
    stripOffsetPx: {
      type: Number,
      default: 40
    }
  },
  data: function () {
    return {
      busy: false,
      errorMessage: '',
      comparePlansOpen: false
    }
  },
  computed: {
    overlayStyle: function () {
      return { top: this.stripOffsetPx + 'px' }
    },
    selectedPlanKey: function () {
      var summary = this.$root.subscriptionSummary
      var isPro = summary ? isProPlanFromSummary(summary) : false
      return displaySelectedPlanKeyFromContext(summary, isPro)
    }
  },
  methods: {
    goBilling: function (plan) {
      var path = '/settings/billing'
      var query = {}
      if (plan === 'starter') query.plan = 'starter'
      if (plan === 'pro') query.upgrade = 'pro'
      if (this.$route.path !== path || JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        this.$router.push({ path: path, query: query })
      }
    },
    startProUpgradeFlow: function () {
      var self = this
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      resolveHasPaymentMethod(this).then(function (hasPm) {
        if (!hasPm) {
          self.busy = false
          if (self.$root) self.$root.pendingUpgradePlan = 'pro'
          self.goBilling('pro')
          return
        }
        return upgradeSubscriptionToPro(self).then(
          function () {
            self.busy = false
          },
          function (res) {
            self.busy = false
            var body = res && res.body
            self.errorMessage =
              (body && body.message) || 'Upgrade failed. Add or update your payment method on billing.'
            if (self.$root) self.$root.pendingUpgradePlan = 'pro'
            self.goBilling('pro')
          }
        )
      }, function () {
        self.busy = false
        if (self.$root) self.$root.pendingUpgradePlan = 'pro'
        self.goBilling('pro')
      })
    },
    signOut: function () {
      if (this.$root.signOutUser) {
        this.$root.signOutUser()
      }
    },
    openComparePlans: function () {
      this.comparePlansOpen = true
    },
    closeComparePlans: function () {
      this.comparePlansOpen = false
    },
    onCompareSelectStarter: function () {
      this.closeComparePlans()
      this.startStarterUpgradeFlow()
    },
    startStarterUpgradeFlow: function () {
      var self = this
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      resolveHasPaymentMethod(this).then(function (hasPm) {
        if (!hasPm) {
          self.busy = false
          if (self.$root) self.$root.pendingUpgradePlan = 'starter'
          self.goBilling('starter')
          return
        }
        return upgradeSubscriptionToPlan(self, 'starter').then(
          function () {
            self.busy = false
          },
          function (res) {
            self.busy = false
            var body = res && res.body
            self.errorMessage =
              (body && body.message) || 'Upgrade failed. Add or update your payment method on billing.'
            if (self.$root) self.$root.pendingUpgradePlan = 'starter'
            self.goBilling('starter')
          }
        )
      }, function () {
        self.busy = false
        if (self.$root) self.$root.pendingUpgradePlan = 'starter'
        self.goBilling('starter')
      })
    },
    onCompareSelectPro: function () {
      this.closeComparePlans()
      this.startProUpgradeFlow()
    }
  }
}
</script>



// WEBPACK FOOTER //
// TrialLockoutOverlay.vue?9804c72e